import { useEffect, useRef, useState } from "react";

import cursorInput from "@/assets/figma/cursor/input.svg";
import cursorInteractive from "@/assets/figma/cursor/interactive.svg";
import cursorLineLong from "@/assets/figma/cursor/line-long.svg";
import cursorLineShort from "@/assets/figma/cursor/line-short.svg";
import cursorPointer from "@/assets/figma/cursor/pointer.svg";

export type MerryCursorVariant = "pointer" | "input" | "interactive";

const cursorAssets = {
  input: cursorInput,
  interactive: cursorInteractive,
  pointer: cursorPointer,
} satisfies Record<MerryCursorVariant, string>;

const cursorNodeIds = {
  input: "392:82729",
  interactive: "439:83021",
  pointer: "392:82724",
} satisfies Record<MerryCursorVariant, string>;

const cursorHotspots = {
  input: { x: 12.5, y: 12 },
  interactive: { x: 10.5, y: 1 },
  pointer: { x: 7, y: 3 },
} satisfies Record<MerryCursorVariant, { x: number; y: number }>;

const inputCursorSelector = [
  "textarea",
  "[contenteditable]:not([contenteditable='false'])",
  "[role='textbox']",
  "input:not([type])",
  "input[type='email']",
  "input[type='number']",
  "input[type='password']",
  "input[type='search']",
  "input[type='tel']",
  "input[type='text']",
  "input[type='url']",
].join(",");

const interactiveCursorSelector = [
  "a[href]",
  "button",
  "label",
  "select",
  "summary",
  "input[type='button']",
  "input[type='checkbox']",
  "input[type='color']",
  "input[type='file']",
  "input[type='radio']",
  "input[type='range']",
  "input[type='reset']",
  "input[type='submit']",
  "[role='button']",
  "[role='checkbox']",
  "[role='link']",
  "[role='menuitem']",
  "[role='option']",
  "[role='radio']",
  "[role='switch']",
  "[role='tab']",
].join(",");

function isCursorVariant(value: string | undefined): value is MerryCursorVariant {
  return value === "pointer" || value === "input" || value === "interactive";
}

function resolveCursorVariant(target: EventTarget | null): MerryCursorVariant {
  if (!(target instanceof Element)) {
    return "pointer";
  }

  const override = target.closest<HTMLElement>("[data-merry-cursor]");
  const overrideVariant = override?.dataset.merryCursor;

  if (isCursorVariant(overrideVariant)) {
    return overrideVariant;
  }

  if (target.closest(inputCursorSelector)) {
    return "input";
  }

  const interactiveTarget = target.closest<HTMLElement>(
    interactiveCursorSelector,
  );

  if (
    interactiveTarget &&
    !interactiveTarget.matches(":disabled, [aria-disabled='true']")
  ) {
    return "interactive";
  }

  return "pointer";
}

function positionCursor(
  root: HTMLDivElement,
  clientX: number,
  clientY: number,
  variant: MerryCursorVariant,
) {
  const hotspot = cursorHotspots[variant];
  root.style.transform = `translate3d(${clientX - hotspot.x}px, ${clientY - hotspot.y}px, 0)`;
}

export function MerryCursor() {
  const rootRef = useRef<HTMLDivElement>(null);
  const variantRef = useRef<MerryCursorVariant>("pointer");
  const isVisibleRef = useRef(false);
  const [variant, setVariant] = useState<MerryCursorVariant>("pointer");
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const finePointerQuery = window.matchMedia(
      "(hover: hover) and (pointer: fine)",
    );
    const documentRoot = document.documentElement;

    const hideCursor = () => {
      if (isVisibleRef.current) {
        isVisibleRef.current = false;
        setIsVisible(false);
      }

      delete documentRoot.dataset.merryCustomCursor;
    };

    const handlePointerMove = (event: PointerEvent) => {
      const root = rootRef.current;

      if (!root || !finePointerQuery.matches || event.pointerType !== "mouse") {
        hideCursor();
        return;
      }

      const nextVariant = resolveCursorVariant(event.target);

      if (variantRef.current !== nextVariant) {
        variantRef.current = nextVariant;
        setVariant(nextVariant);
      }

      positionCursor(root, event.clientX, event.clientY, nextVariant);

      if (!isVisibleRef.current) {
        isVisibleRef.current = true;
        setIsVisible(true);
      }

      documentRoot.dataset.merryCustomCursor = "active";
    };

    const handlePointerCapabilityChange = () => {
      if (!finePointerQuery.matches) {
        hideCursor();
      }
    };

    document.addEventListener("pointermove", handlePointerMove, {
      passive: true,
    });
    document.addEventListener("pointerleave", hideCursor);
    document.addEventListener("pointercancel", hideCursor);
    document.addEventListener("keydown", hideCursor);
    window.addEventListener("blur", hideCursor);
    finePointerQuery.addEventListener("change", handlePointerCapabilityChange);

    return () => {
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerleave", hideCursor);
      document.removeEventListener("pointercancel", hideCursor);
      document.removeEventListener("keydown", hideCursor);
      window.removeEventListener("blur", hideCursor);
      finePointerQuery.removeEventListener(
        "change",
        handlePointerCapabilityChange,
      );
      delete documentRoot.dataset.merryCustomCursor;
    };
  }, []);

  return (
    <div
      ref={rootRef}
      aria-hidden="true"
      className="merry-cursor"
      data-cursor-variant={variant}
      data-cursor-visible={isVisible ? "true" : "false"}
      data-figma-component-id="392:82728"
      data-figma-node-id={cursorNodeIds[variant]}
      data-merry-cursor-root=""
    >
      <img
        alt=""
        className="merry-cursor__asset"
        data-merry-cursor-asset=""
        src={cursorAssets[variant]}
      />

      {variant === "interactive" ? (
        <span className="merry-cursor__interaction-lines">
          <span
            className="merry-cursor__line merry-cursor__line--long"
            data-figma-node-id="439:83039"
          >
            <span className="merry-cursor__line-rotation">
              <span className="merry-cursor__line-frame merry-cursor__line-frame--long">
                <img alt="" src={cursorLineLong} />
              </span>
            </span>
          </span>
          <span
            className="merry-cursor__line merry-cursor__line--short-first"
            data-figma-node-id="439:83041"
          >
            <span className="merry-cursor__line-rotation">
              <span className="merry-cursor__line-frame merry-cursor__line-frame--short">
                <img alt="" src={cursorLineShort} />
              </span>
            </span>
          </span>
          <span
            className="merry-cursor__line merry-cursor__line--short-last"
            data-figma-node-id="439:83043"
          >
            <span className="merry-cursor__line-rotation">
              <span className="merry-cursor__line-frame merry-cursor__line-frame--short">
                <img alt="" src={cursorLineShort} />
              </span>
            </span>
          </span>
        </span>
      ) : null}
    </div>
  );
}
